import React, { useState, useEffect } from 'react';
import { Button, Select, Avatar, Tag, Radio, Spin } from 'antd';
import { LeftOutlined, RightOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import 'moment-jalaali';
import ShiftUpdateModal from './ShiftUpdateModal';
import GroupShiftAssignmentModal from './GroupShiftAssignmentModal';
import { shiftsApi, employeesApi, departmentsApi } from '../../../utils/api';
import { message } from 'antd';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const { Option } = Select;

const ShiftsScheduleView = ({ groupModalVisible, onGroupModalClose, onExportParamsChange }) => {
  const [viewType, setViewType] = useState('week'); // 'week' or 'month'
  const [currentWeek, setCurrentWeek] = useState(moment());
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedCell, setSelectedCell] = useState(null);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shiftsData, setShiftsData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // بارگذاری داده‌ها
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadShifts();
  }, [viewType, currentWeek, currentMonth, selectedEmployee, selectedDepartment]);

  useEffect(() => {
    if (!onExportParamsChange) return;
    const weekDays = getWeekDays();
    const weekStart = weekDays[0];
    onExportParamsChange({
      viewType,
      startDate: viewType === 'week'
        ? weekStart.format('jYYYY-jMM-jDD')
        : currentMonth.clone().startOf('month').format('jYYYY-jMM-jDD'),
      id: selectedEmployee,
      department: selectedDepartment,
    });
  }, [viewType, currentWeek, currentMonth, selectedEmployee, selectedDepartment, onExportParamsChange]);

  const loadInitialData = async () => {
    setEmployeesLoading(true);
    setDepartmentsLoading(true);
    try {
      const [empRes, deptRes] = await Promise.all([
        employeesApi.getAll(),
        departmentsApi.getAll(),
      ]);
      setEmployees(empRes.data || empRes || []);
      setDepartments(deptRes.data || []);
    } catch (error) {
      console.error('خطا در دریافت داده‌ها:', error);
      message.error('خطا در دریافت داده‌ها');
    } finally {
      setEmployeesLoading(false);
      setDepartmentsLoading(false);
    }
  };

  const loadShifts = async () => {
    setLoading(true);
    try {
      const params = {
        userId: selectedEmployee,
        department: selectedDepartment,
        view_type: viewType,
      };

      if (viewType === 'week') {
        const weekDays = getWeekDays();
        const weekStart = weekDays[0];
        params.year = weekStart.format('jYYYY');
        params.month = weekStart.format('jMM');
        params.week_start_date = weekStart.format('jYYYY/jMM/jDD');
      } else {
        params.year = currentMonth.format('jYYYY');
        params.month = currentMonth.format('jMM');
      }

      const res = await shiftsApi.getAll(params);
      setShiftsData(res.data || null);
    } catch (error) {
      console.error('خطا در دریافت شیفت‌ها:', error);
      message.error('خطا در دریافت شیفت‌ها');
    } finally {
      setLoading(false);
    }
  };

  // محاسبه روزهای هفته
  const getWeekDays = () => {
    const startOfWeek = currentWeek.clone().startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.clone().add(i, 'days'));
    }
    return days;
  };

  const weekDays = getWeekDays();

  const handlePrevWeek = () => {
    setCurrentWeek(currentWeek.clone().subtract(1, 'week'));
  };

  const handleNextWeek = () => {
    setCurrentWeek(currentWeek.clone().add(1, 'week'));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  const handleCellClick = (employeeId, date) => {
    setSelectedCell({ employeeId, date });
    setUpdateModalVisible(true);
  };

  const handleSaveShift = async (values) => {
    // این تابع دیگر استفاده نمی‌شود چون ShiftUpdateModal خودش API را صدا می‌زند
    // اما برای سازگاری با backward compatibility نگه می‌داریم
    await loadShifts();
    setUpdateModalVisible(false);
    setSelectedCell(null);
  };

  const formatDateHeader = (date) => {
    const jDate = moment(date);
    const dayName = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'][jDate.day()];
    const day = jDate.jDate();
    const monthName = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'][jDate.jMonth()];
    return `${dayName} ${day} ${monthName}`;
  };

  const getShiftForCell = (employeeId, date) => {
    if (!shiftsData || !shiftsData.employees) return null;
    
    const employee = shiftsData.employees.find(emp => emp.id === employeeId);
    if (!employee || !employee.days) return null;

    if (viewType === 'week') {
      const dateStr = moment(date).format('YYYY-MM-DD');
      return employee.days[dateStr] || null;
    } else {
      // در نمای ماهانه، بک‌اند ممکن است کلیدها را با فرمت‌های مختلف بفرستد:
      // - "01" تا "31"
      // - "1" تا "31"
      // - یا حتی "YYYY-MM-DD"
      const m = moment(date);
      const dayNumber = m.format('jDD');          // "01"
      const dayNumberNoZero = String(m.jDate());  // "1"
      const gregorianDateStr = m.format('YYYY-MM-DD');

      return (
        employee.days[dayNumber] ||
        employee.days[dayNumberNoZero] ||
        employee.days[Number(dayNumber)] ||
        employee.days[gregorianDateStr] ||
        null
      );
    }
  };

  // محاسبه روزهای ماه برای نمایش ماهانه
  const getMonthDays = () => {
    const startOfMonth = currentMonth.clone().startOf('jMonth');
    const endOfMonth = currentMonth.clone().endOf('jMonth');
    const days = [];
    const current = startOfMonth.clone();
    
    while (current.isSameOrBefore(endOfMonth)) {
      days.push(current.clone());
      current.add(1, 'day');
    }
    
    return days;
  };

  const monthDays = getMonthDays();
  const monthEmployees = shiftsData?.employees || [];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* کنترل‌های فیلتر و تاریخ */}
      <div className="mb-4 flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          <Select
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            style={{ width: 170, fontFamily: 'CustomFont' }}
            placeholder="انتخاب کارمند"
            loading={employeesLoading}
            allowClear
          >
            <Option value="all">همه کارمندان</Option>
            {employees.map((emp) => (
              <Option key={emp.id} value={String(emp.id)}>
                {emp.name}
              </Option>
            ))}
          </Select>
          <Select
            value={selectedDepartment}
            onChange={setSelectedDepartment}
            style={{ width: 170, fontFamily: 'CustomFont' }}
            placeholder="انتخاب دپارتمان"
            loading={departmentsLoading}
            allowClear
          >
            <Option value="all">همه دپارتمان‌ها</Option>
            {departments.map((dept) => (
              <Option key={dept.id} value={String(dept.id)}>
                {dept.team_name || dept.name}
              </Option>
            ))}
          </Select>
          <Radio.Group
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            style={{ fontFamily: 'CustomFont' }}
            size="small"
          >
            <Radio.Button value="week">هفتگی</Radio.Button>
            <Radio.Button value="month">ماهانه</Radio.Button>
          </Radio.Group>
        </div>

        <div className="flex items-center gap-2 w-full lg:w-auto justify-between lg:justify-start">
          <Button
            icon={<RightOutlined />}
            onClick={viewType === 'week' ? handlePrevWeek : handlePrevMonth}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
          />
          <span className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 dark:text-gray-200 px-2 sm:px-4 text-center break-words max-w-[60vw] sm:max-w-none" style={{ fontFamily: 'CustomFont', lineHeight: 1.5 }}>
            {viewType === 'week' 
              ? `${formatDateHeader(weekDays[0])} - ${formatDateHeader(weekDays[6])}`
              : currentMonth.format('jMMMM jYYYY')
            }
          </span>
          <Button
            icon={<LeftOutlined />}
            onClick={viewType === 'week' ? handleNextWeek : handleNextMonth}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 flex items-center gap-4 flex-wrap" style={{ fontFamily: 'CustomFont' }}>
        <div className="flex items-center gap-2">
          <span className="text-green-500 text-xl">★</span>
          <span className="text-gray-600 dark:text-gray-400">: تعطیلی</span>
        </div>
        <Tag color="blue" style={{ fontFamily: 'CustomFont' }}>
          GS: General Shift
        </Tag>
      </div>

      {/* جدول برنامه شیفت‌ها */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {loading ? (
          <div className="p-6">
            <TableSkeleton rows={5} columns={viewType === 'week' ? 8 : 32} />
          </div>
        ) : !shiftsData || !shiftsData.employees || shiftsData.employees.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
            هیچ کارمندی یافت نشد
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-full" style={{ fontFamily: 'CustomFont' }}>
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <th className="p-4 text-right font-bold text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 sticky right-0 bg-gray-50 dark:bg-gray-900 z-10 min-w-[200px]">
                    کارمند
                  </th>
                  {viewType === 'week' ? (
                    weekDays.map((day, index) => (
                      <th
                        key={index}
                        className="p-4 text-center font-bold text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 min-w-[120px]"
                      >
                        <div className="text-sm">{formatDateHeader(day)}</div>
                      </th>
                    ))
                  ) : (
                    monthDays.map((day, index) => (
                      <th
                        key={index}
                        className="p-3 text-center font-bold text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 min-w-[60px]"
                      >
                        <div className="text-xs">{day.format('jDD')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'][day.day()].substring(0, 2)}
                        </div>
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {monthEmployees.map((employee) => (
                  <tr key={employee.id} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="p-4 border-r border-gray-200 dark:border-gray-700 sticky right-0 bg-white dark:bg-gray-800 z-10">
                      <div className="flex items-center gap-3">
                        <Avatar
                          size={40}
                          src={employee.avatar}
                          style={{ backgroundColor: '#1890ff' }}
                        >
                          {employee.name?.charAt(0) || '?'}
                        </Avatar>
                        <div>
                          <div className="font-semibold text-gray-800 dark:text-gray-200">
                            {employee.name}
                          </div>
                          {employee.designation_name && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {employee.designation_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {viewType === 'week' ? (
                      weekDays.map((day, dayIndex) => {
                        const shift = getShiftForCell(employee.id, day);
                        return (
                          <td
                            key={dayIndex}
                            className="p-2 border-r border-gray-200 dark:border-gray-700 text-center"
                          >
                            {shift && shift.type !== 'empty' ? (
                              <div
                                className={`rounded p-2 cursor-pointer transition-colors ${
                                  shift.type === 'holiday'
                                    ? 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'
                                    : 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                                }`}
                                onClick={() => handleCellClick(employee.id, day)}
                              >
                                {shift.type === 'holiday' && (
                                  <span className="text-green-500 text-lg">★</span>
                                )}
                                {shift.shift_name && (
                                  <div className={`text-xs font-semibold mt-1 ${
                                    shift.type === 'holiday'
                                      ? 'text-green-800 dark:text-green-200'
                                      : 'text-blue-800 dark:text-blue-200'
                                  }`} style={{ fontFamily: 'CustomFont' }}>
                                    {shift.shift_name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full min-h-[48px]">
                                <Button
                                  type="text"
                                  icon={<PlusOutlined />}
                                  className="w-12 h-12 p-0 m-0 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border-green-200 dark:border-green-800 rounded flex items-center justify-center"
                                  style={{ minWidth: '48px', maxWidth: '48px', padding: 0 }}
                                  onClick={() => handleCellClick(employee.id, day)}
                                />
                              </div>
                            )}
                          </td>
                        );
                      })
                    ) : (
                      monthDays.map((day, dayIndex) => {
                        const shift = getShiftForCell(employee.id, day);
                        return (
                          <td
                            key={dayIndex}
                            className="p-1 border-r border-gray-200 dark:border-gray-700 text-center"
                          >
                            {shift && shift.type !== 'empty' ? (
                              <div
                                className={`rounded p-1 cursor-pointer transition-colors text-xs ${
                                  shift.type === 'holiday'
                                    ? 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'
                                    : 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                                }`}
                                onClick={() => handleCellClick(employee.id, day)}
                                title={shift.shift_name || ''}
                              >
                                {shift.type === 'holiday' ? '★' : shift.shift_code || 'GS'}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full min-h-[32px]">
                                <Button
                                  type="text"
                                  icon={<PlusOutlined />}
                                  className="w-8 h-8 p-0 m-0 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border-green-200 dark:border-green-800 rounded flex items-center justify-center"
                                  style={{ minWidth: '32px', maxWidth: '32px', padding: 0 }}
                                  onClick={() => handleCellClick(employee.id, day)}
                                />
                              </div>
                            )}
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal به‌روزرسانی شیفت */}
      <ShiftUpdateModal
        visible={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          setSelectedCell(null);
        }}
        onSave={handleSaveShift}
        employee={selectedCell ? monthEmployees.find(e => e.id === selectedCell.employeeId) : null}
        date={selectedCell ? selectedCell.date : null}
        shift={selectedCell ? getShiftForCell(selectedCell.employeeId, selectedCell.date) : null}
      />

      {/* Modal اختصاص گروهی */}
      <GroupShiftAssignmentModal
        visible={groupModalVisible}
        onCancel={onGroupModalClose}
        onSave={async (values) => {
          try {
            setLoading(true);
            await shiftsApi.bulkShift(values);
            message.success('شیفت‌های گروهی با موفقیت ثبت شدند');
            await loadShifts();
            onGroupModalClose();
          } catch (error) {
            console.error('خطا در ثبت شیفت‌های گروهی:', error);
            message.error('خطا در ثبت شیفت‌های گروهی');
          } finally {
            setLoading(false);
          }
        }}
        employees={employees}
        departments={departments}
      />
    </div>
  );
};

export default ShiftsScheduleView;
